/**
 * WORKBENCH PAGE — Hub unificado de mapas VSM, ações e projetos Lean
 *
 * Três abas:
 *  • Mapas   — lista todos os projetos VSM com status, contagens e último acesso
 *  • Ações   — todas as ações 5W2H de todos os projetos em uma única lista
 *  • Projetos — módulo de Projetos Lean (ConsultingProjectPage embutido)
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Map,
  CheckSquare,
  Plus,
  Search,
  Home,
  Clock,
  LayoutGrid,
  List,
  Circle,
  Layers,
  ChevronRight,
} from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import {
  RibbonDesktopButton,
  desktopRibbonDividerClass,
  desktopRibbonGroupClass,
  desktopRibbonInputClass,
  desktopRibbonSelectClass,
} from '../components/ribbon/DesktopRibbonControls';
import { useAuth } from '../context/AuthContext';
import type { Project } from '../types/project';
import type { Action } from '../types/action';

// ─── Types ───────────────────────────────────────────────────────────────────

type WorkbenchTab = 'maps' | 'actions';
type MapFilter = 'all' | 'draft' | 'master' | 'archived';
type ActionStatusFilter = 'all' | 'todo' | 'doing' | 'done' | 'canceled';

type EnrichedAction = Action & { _projectName: string; _projectId: string };

interface WorkbenchPageProps {
  onGoHome?: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getProjectStatus(p: Project): { label: string; color: string; bg: string } {
  if (p.visibility === 'archived')
    return { label: 'Arquivado', color: 'text-[var(--text-muted)]', bg: 'bg-[var(--bg-surface-3)] border border-[var(--border-default)]' };
  if (p.status === 'master')
    return { label: 'Finalizado', color: 'text-blue-500', bg: 'bg-blue-500/10 border border-blue-500/30' };
  if (p.visibility === 'published')
    return { label: 'Publicado', color: 'text-emerald-500', bg: 'bg-emerald-500/10 border border-emerald-500/30' };
  return { label: 'Rascunho', color: 'text-amber-500', bg: 'bg-amber-500/10 border border-amber-500/30' };
}

function getCardCount(p: Project): number {
  return p.estado_atual.sheets.reduce((acc, s) => acc + (s.cards?.length ?? 0), 0);
}

function getActionCount(p: Project): number {
  return p.estado_atual.actions?.length ?? 0;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'agora';
  if (mins < 60) return `${mins}m atrás`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d atrás`;
  return new Date(iso).toLocaleDateString('pt-BR');
}

const ACTION_STATUS_LABEL: Record<string, string> = {
  todo:     'A fazer',
  doing:    'Em andamento',
  done:     'Concluída',
  canceled: 'Cancelada',
};

const ACTION_STATUS_COLOR: Record<string, string> = {
  todo:     'text-[var(--text-secondary)]',
  doing:    'text-amber-500',
  done:     'text-emerald-500',
  canceled: 'text-[var(--text-muted)]',
};

// ─── Component ───────────────────────────────────────────────────────────────

export const WorkbenchPage: React.FC<WorkbenchPageProps> = ({ onGoHome }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { projects, setCurrentProjectId } = useProjectStore();

  const [activeTab, setActiveTab] = useState<WorkbenchTab>('maps');
  const [searchQuery, setSearchQuery] = useState('');
  const [mapFilter, setMapFilter] = useState<MapFilter>('all');
  const [actionStatusFilter, setActionStatusFilter] = useState<ActionStatusFilter>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const organizationId = user?.organizationId ?? '';

  const switchTab = (tab: WorkbenchTab) => {
    setActiveTab(tab);
    setSearchQuery('');
  };

  const handleOpenMap = (project: Project) => {
    setCurrentProjectId(project.id);
    navigate('/mapa');
  };

  // ─── Derived data ──────────────────────────────────────────────────────────

  const mapCounts = useMemo(() => ({
    all:      projects.length,
    draft:    projects.filter(p => !p.status || p.status === 'draft').length,
    master:   projects.filter(p => p.status === 'master').length,
    archived: projects.filter(p => p.visibility === 'archived').length,
  }), [projects]);

  const filteredMaps = useMemo(() => {
    let list = [...projects];
    if (mapFilter === 'draft')    list = list.filter(p => !p.status || p.status === 'draft');
    if (mapFilter === 'master')   list = list.filter(p => p.status === 'master');
    if (mapFilter === 'archived') list = list.filter(p => p.visibility === 'archived');
    if (searchQuery) list = list.filter(p => p.nome_projeto.toLowerCase().includes(searchQuery.toLowerCase()));
    return list.sort((a, b) => new Date(b.ultimo_salvamento).getTime() - new Date(a.ultimo_salvamento).getTime());
  }, [projects, mapFilter, searchQuery]);

  const allActions = useMemo<EnrichedAction[]>(() =>
    projects.flatMap(p =>
      (p.estado_atual.actions ?? []).map(a => ({ ...a, _projectName: p.nome_projeto, _projectId: p.id }))
    ),
    [projects]
  );

  const actionCounts = useMemo(() => ({
    all:      allActions.length,
    todo:     allActions.filter(a => a.status === 'todo').length,
    doing:    allActions.filter(a => a.status === 'doing').length,
    done:     allActions.filter(a => a.status === 'done').length,
    canceled: allActions.filter(a => a.status === 'canceled').length,
  }), [allActions]);

  const filteredActions = useMemo(() => {
    let list = allActions;
    if (actionStatusFilter !== 'all') list = list.filter(a => (a.status as string) === actionStatusFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(a =>
        a.title?.toLowerCase().includes(q) ||
        a._projectName.toLowerCase().includes(q) ||
        (a.responsible ?? a.responsavel)?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [allActions, actionStatusFilter, searchQuery]);

  // ─── Ribbon ────────────────────────────────────────────────────────────────
  // Not shown when consulting tab is active (ConsultingProjectPage has its own ribbon)

  const renderRibbon = () => (
    <div
      data-ribbon="true"
      className="w-full enterprise-ribbon-zone officebar-surface enterprise-font z-[9999] text-[var(--ribbon-text-primary)]"
    >
      <div
        className="relative flex items-stretch gap-0 overflow-x-auto overflow-y-visible py-0"
        style={{
          height: 'var(--shell-ribbon-height, 48px)',
          paddingLeft: 'var(--shell-content-inset, 10px)',
          paddingRight: 'var(--shell-ribbon-inline-gap, 10px)',
        }}
      >
        {/* Início */}
        <div className={desktopRibbonGroupClass}>
          <RibbonDesktopButton
            icon={<Home size={18} strokeWidth={1.5} />}
            label="Início"
            onClick={() => onGoHome?.()}
          />
        </div>

        <div className={desktopRibbonDividerClass} />

        {/* Abas principais */}
        <div className={desktopRibbonGroupClass}>
          <RibbonDesktopButton
            icon={<Map size={18} strokeWidth={1.5} />}
            label="Mapas"
            active={activeTab === 'maps'}
            onClick={() => switchTab('maps')}
          />
          <RibbonDesktopButton
            icon={<CheckSquare size={18} strokeWidth={1.5} />}
            label="Ações"
            active={activeTab === 'actions'}
            onClick={() => switchTab('actions')}
          />
        </div>

        {/* Ações da aba Mapas */}
        {activeTab === 'maps' && (
          <>
            <div className={desktopRibbonDividerClass} />
            <div className={desktopRibbonGroupClass}>
              <RibbonDesktopButton
                icon={<Plus size={18} strokeWidth={1.5} />}
                label="Novo Mapa"
                onClick={() => navigate('/mapa')}
              />
              <RibbonDesktopButton
                icon={<LayoutGrid size={18} strokeWidth={1.5} />}
                label="Grade"
                active={viewMode === 'grid'}
                onClick={() => setViewMode('grid')}
              />
              <RibbonDesktopButton
                icon={<List size={18} strokeWidth={1.5} />}
                label="Lista"
                active={viewMode === 'list'}
                onClick={() => setViewMode('list')}
              />
            </div>
          </>
        )}

        {/* Busca + filtros (Mapas e Ações) */}
        <div className="ml-auto flex h-full items-center">
          <div className={desktopRibbonDividerClass} />
          <div className="flex w-[200px] items-center rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 mx-2">
            <Search size={13} className="shrink-0 text-[var(--text-secondary)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder={activeTab === 'maps' ? 'Buscar mapas...' : 'Buscar ações...'}
              className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-2`}
            />
          </div>
          <div className={desktopRibbonDividerClass} />
          <div className={`${desktopRibbonGroupClass} px-2`}>
            {activeTab === 'maps' && (
              <select
                value={mapFilter}
                onChange={e => setMapFilter(e.target.value as MapFilter)}
                className={desktopRibbonSelectClass}
              >
                <option value="all">Status: Todos ({mapCounts.all})</option>
                <option value="draft">Status: Rascunho ({mapCounts.draft})</option>
                <option value="master">Status: Finalizado ({mapCounts.master})</option>
                <option value="archived">Status: Arquivado ({mapCounts.archived})</option>
              </select>
            )}
            {activeTab === 'actions' && (
              <select
                value={actionStatusFilter}
                onChange={e => setActionStatusFilter(e.target.value as ActionStatusFilter)}
                className={desktopRibbonSelectClass}
              >
                <option value="all">Status: Todos ({actionCounts.all})</option>
                <option value="todo">Status: A fazer ({actionCounts.todo})</option>
                <option value="doing">Status: Em andamento ({actionCounts.doing})</option>
                <option value="done">Status: Concluídas ({actionCounts.done})</option>
                <option value="canceled">Status: Canceladas ({actionCounts.canceled})</option>
              </select>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Maps Tab ──────────────────────────────────────────────────────────────

  const renderMapsTab = () => (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex-1 overflow-auto bg-[var(--bg-workspace)] p-4">
        {filteredMaps.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-surface)]">
              <Map size={28} className="text-[var(--text-muted)]" />
            </div>
            <p className="text-sm font-medium text-[var(--text-primary)]">Nenhum mapa encontrado</p>
            <p className="mt-1 text-xs text-[var(--text-muted)]">Crie um novo mapa VSM para começar.</p>
            <button
              onClick={() => navigate('/mapa')}
              className="mt-4 flex items-center gap-2 rounded-[4px] bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-500"
            >
              <Plus size={14} /> Novo Mapa
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredMaps.map(p => {
              const status = getProjectStatus(p);
              const cardCount = getCardCount(p);
              const actionCount = getActionCount(p);
              return (
                <button
                  key={p.id}
                  onClick={() => handleOpenMap(p)}
                  className="group flex flex-col items-start gap-3 rounded-[6px] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 text-left transition-colors hover:border-[var(--brand-primary,#10b981)] hover:bg-[var(--bg-surface-3)]"
                >
                  <div className="flex w-full items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <Map size={14} strokeWidth={1.5} className="shrink-0 text-[var(--text-muted)]" />
                      <span className="truncate text-sm font-semibold text-[var(--text-primary)]">{p.nome_projeto}</span>
                    </div>
                    <span className={`shrink-0 rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium ${status.color} ${status.bg}`}>
                      {status.label}
                    </span>
                  </div>

                  <div className="flex w-full items-center gap-3 text-[11px] text-[var(--text-muted)]">
                    <span className="flex items-center gap-1">
                      <Layers size={11} strokeWidth={1.5} />
                      {p.estado_atual.sheets.length} folha{p.estado_atual.sheets.length !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Circle size={11} strokeWidth={1.5} />
                      {cardCount} nó{cardCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckSquare size={11} strokeWidth={1.5} />
                      {actionCount} ação{actionCount !== 1 ? 'ões' : ''}
                    </span>
                  </div>

                  <div className="flex w-full items-center justify-between">
                    <span className="flex items-center gap-1 text-[10px] text-[var(--text-muted)]">
                      <Clock size={10} strokeWidth={1.5} />
                      {formatRelativeTime(p.ultimo_salvamento)}
                    </span>
                    <ChevronRight size={14} className="text-[var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          /* List mode */
          <div className="overflow-hidden rounded-[6px] border border-[var(--border-default)]">
            <div className="grid grid-cols-[1fr_90px_60px_60px_80px_100px_24px] items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-surface-3)] px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              <span>Nome</span><span>Status</span><span>Folhas</span><span>Nós</span><span>Ações</span><span>Editado</span><span />
            </div>
            <div className="flex flex-col divide-y divide-[var(--border-default)]">
              {filteredMaps.map(p => {
                const status = getProjectStatus(p);
                const cardCount = getCardCount(p);
                const actionCount = getActionCount(p);
                return (
                  <button
                    key={p.id}
                    onClick={() => handleOpenMap(p)}
                    className="group grid grid-cols-[1fr_90px_60px_60px_80px_100px_24px] items-center gap-2 bg-[var(--bg-surface)] px-4 py-2.5 text-left transition-colors hover:bg-[var(--bg-surface-3)]"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Map size={13} strokeWidth={1.5} className="shrink-0 text-[var(--text-muted)]" />
                      <span className="truncate text-xs font-medium text-[var(--text-primary)]">{p.nome_projeto}</span>
                    </span>
                    <span className={`rounded-[3px] px-1.5 py-0.5 text-[10px] font-medium ${status.color} ${status.bg}`}>{status.label}</span>
                    <span className="text-center text-xs text-[var(--text-secondary)]">{p.estado_atual.sheets.length}</span>
                    <span className="text-center text-xs text-[var(--text-secondary)]">{cardCount}</span>
                    <span className="text-center text-xs text-[var(--text-secondary)]">{actionCount}</span>
                    <span className="text-right text-[10px] text-[var(--text-muted)]">{formatRelativeTime(p.ultimo_salvamento)}</span>
                    <ChevronRight size={14} className="text-[var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Actions Tab ───────────────────────────────────────────────────────────

  const renderActionsTab = () => (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex-1 overflow-auto bg-[var(--bg-workspace)] p-4">
        {filteredActions.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded border border-[var(--border-default)] bg-[var(--bg-surface)]">
              <CheckSquare size={28} className="text-[var(--text-muted)]" />
            </div>
            <p className="text-sm font-medium text-[var(--text-primary)]">Nenhuma ação encontrada</p>
            <p className="mt-1 text-xs text-[var(--text-muted)]">As ações são criadas dentro dos mapas VSM.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-[6px] border border-[var(--border-default)]">
            <div className="grid grid-cols-[1fr_140px_110px_90px_80px] items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-surface-3)] px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              <span>Ação (O quê)</span>
              <span>Projeto</span>
              <span>Responsável</span>
              <span>Prazo</span>
              <span>Status</span>
            </div>
            <div className="flex flex-col divide-y divide-[var(--border-default)]">
              {filteredActions.map((a, i) => (
                <button
                  key={a.id ?? i}
                  onClick={() => {
                    setCurrentProjectId(a._projectId);
                    navigate('/acoes');
                  }}
                  className="group grid grid-cols-[1fr_140px_110px_90px_80px] items-center gap-2 bg-[var(--bg-surface)] px-4 py-2.5 text-left transition-colors hover:bg-[var(--bg-surface-3)]"
                >
                  <span className="truncate text-xs font-medium text-[var(--text-primary)]">{a.title || '—'}</span>
                  <span className="truncate text-xs text-[var(--text-secondary)]">{a._projectName}</span>
                  <span className="truncate text-xs text-[var(--text-secondary)]">{a.responsible ?? a.responsavel ?? '—'}</span>
                  <span className="text-xs text-[var(--text-secondary)]">
                    {(a.deadline ?? a.prazo) ? new Date((a.deadline ?? a.prazo)!).toLocaleDateString('pt-BR') : '—'}
                  </span>
                  <span className={`text-[10px] font-medium ${ACTION_STATUS_COLOR[a.status] ?? 'text-[var(--text-muted)]'}`}>
                    {ACTION_STATUS_LABEL[a.status] ?? a.status}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Root render ───────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {renderRibbon()}

      {activeTab === 'maps'    && renderMapsTab()}
      {activeTab === 'actions' && renderActionsTab()}
    </div>
  );
};

export default WorkbenchPage;
